import { REJECTED_SESSION_KEYS, RejectedSessionKey } from 'constants/errorKeys';

import { isSetPasswordRoute } from './authRoutes';

/** The parts of the profile that decide whether a session can be served. */
export interface GateableProfile {
	status?: string;
	organization?: string;
}

export type SessionGate =
	/** Nothing is wrong; the app may render the authenticated routes. */
	| { verdict: 'serve' }
	/** Not decidable yet, or deliberately left alone. Do nothing this pass. */
	| { verdict: 'wait' }
	/** End the session and send the user back to login with this reason. */
	| { verdict: 'reject'; reason: RejectedSessionKey };

const serve: SessionGate = { verdict: 'serve' };
const wait: SessionGate = { verdict: 'wait' };
const reject = (reason: RejectedSessionKey): SessionGate => ({ verdict: 'reject', reason });

/**
 * Decides what to do with an authenticated session whose profile has loaded.
 *
 * The order here is load-bearing, which is why it lives in a pure function with
 * tests rather than inline in App.tsx's effect:
 *
 * The activation check has to come first, because it is the only one decidable
 * from the profile alone. `accessLoaded` covers feature access and user access,
 * and neither is ever fetched for an unactivated user - so putting that gate in
 * front would make a `fresh` profile fall through every pass and land on the
 * router's catch-all LoadingPage, which is exactly the bug this fixed: the user
 * signed in, went nowhere, and saw a login button stuck on "Loading..." with no
 * error. A future refactor that hoists the gate back up should fail a test, not
 * ship silently.
 *
 * The disabled and no-organization checks stay behind `accessLoaded` on purpose:
 * evaluating `!profile.organization` mid-load would read a session that is still
 * resolving as one whose user belongs to no organization.
 */
export const evaluateSession = (profile: GateableProfile, pathname: string, accessLoaded: boolean): SessionGate => {
	if (profile.status === 'fresh') {
		// /setpassword is the activation itself, so a `fresh` profile is expected
		// there. Rejecting would kill the very flow the rejection message points
		// them at.
		return isSetPasswordRoute(pathname) ? wait : reject(REJECTED_SESSION_KEYS.ACCOUNT_NOT_ACTIVATED);
	}

	if (!accessLoaded) {
		return wait;
	}

	if (profile.status === 'disabled') {
		return reject(REJECTED_SESSION_KEYS.ACCOUNT_DISABLED);
	}

	if (!profile.organization) {
		return reject(REJECTED_SESSION_KEYS.NO_ORGANIZATION);
	}

	return serve;
};
